/**
 * usePublicOrg — resolva aktiv organisation utifrån browser-hostname.
 *
 * Används för multi-tenant domain-routing:
 *   bokning.actionpadel.se → AP Nacka
 *   demo.boply.app          → demo
 *
 * Sker FÖRE login så att anonyma besökare hamnar i rätt org-kontext
 * (branding, signup-scoping, public availability).
 *
 * Fallback-ordning:
 *   1. hostname = localhost/127.0.0.1 → `VITE_DEV_DEFAULT_ORG_SLUG` env
 *   1.5. Vercel preview (`*.vercel.app`) → `VITE_PREVIEW_DEFAULT_ORG_SLUG`
 *        env (TASK-76 PR-1). Defense-in-depth: hostname suffix + Vercel
 *        env-var scope (set in *Preview* only — never in Production) make
 *        this branch unreachable from a production deployment. The static
 *        legacy `actionpadel-bokningssystem.vercel.app` row in
 *        `organization_domains_public` still catches that exact URL via (2)
 *        when the env var is unset.
 *        Note: an `import.meta.env.MODE !== "production"` gate was tried
 *        first but Vite's `MODE` reflects the *build command*, not the
 *        deployment environment — `vite build` always emits
 *        MODE='production' (Vercel preview included), making the gate
 *        always-false on Vercel. Removed to fix the broken-on-Vercel case.
 *        See audit-reports/preview-routing/01-investigation.md.
 *   2. hostname finns i `organization_domains` → den org:en
 *      (legacy actionpadel-bokningssystem.vercel.app ligger nu också där)
 *   3. annat → null (→ UnknownHostname-vy)
 *
 * Ref: audit-reports/feat-domain-mapping/rapport.md
 * Ref: audit-reports/feat-domain-mapping-audit/rapport.md F-1 (legacy → DB)
 * Ref: audit-reports/preview-routing/01-investigation.md (TASK-76 PR-1)
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PublicOrg {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  // PR-AM: per-tenant favicon. Sourced from organizations.app_icon_url
  // (set via admin Identitet upload). Drives <link rel="icon"> +
  // <link rel="apple-touch-icon"> via usePublicFavicon (called in
  // HostnameGate before auth resolves).
  app_icon_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  settings: Record<string, unknown>;
}

async function fetchOrgById(id: string): Promise<PublicOrg | null> {
  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, slug, logo_url, app_icon_url, primary_color, secondary_color, settings")
    .eq("id", id)
    .maybeSingle();
  if (error || !data) return null;
  return { ...data, settings: (data.settings as Record<string, unknown>) ?? {} };
}

async function fetchOrgBySlug(slug: string): Promise<PublicOrg | null> {
  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, slug, logo_url, app_icon_url, primary_color, secondary_color, settings")
    .eq("slug", slug)
    .maybeSingle();
  if (error || !data) return null;
  return { ...data, settings: (data.settings as Record<string, unknown>) ?? {} };
}

export function usePublicOrg() {
  return useQuery({
    queryKey: ["public-org", typeof window !== "undefined" ? window.location.hostname : "ssr"],
    queryFn: async (): Promise<PublicOrg | null> => {
      if (typeof window === "undefined") return null;
      const hostname = window.location.hostname.toLowerCase();

      // (1) Dev-läge: localhost → VITE_DEV_DEFAULT_ORG_SLUG
      if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost")) {
        const devSlug = import.meta.env.VITE_DEV_DEFAULT_ORG_SLUG as string | undefined;
        if (devSlug) {
          return fetchOrgBySlug(devSlug);
        }
        return null;
      }

      // (1.5) Preview-läge: Vercel preview deployments → VITE_PREVIEW_DEFAULT_ORG_SLUG
      // (TASK-76 PR-1). Per-deploy preview hostnames look like
      // `actionpadel-bokningssyste-git-<hash>-<owner>.vercel.app` and don't
      // match any row in organization_domains_public, so without this branch
      // every preview lands on <UnknownHostname>. Two-layer defense keeps
      // this branch unreachable in production:
      //   (a) PROD hostnames (bokning.actionpadel.se, demo.boply.app,
      //       custom-domain tenants) never end with `.vercel.app`.
      //   (b) The env var is set in Vercel's *Preview* environment scope
      //       only — not Production — so even if a `.vercel.app` URL
      //       somehow loaded a production build, `previewSlug` would be
      //       undefined and the branch would no-op.
      // (An earlier draft also gated on `MODE !== 'production'`, but Vite
      // emits MODE='production' for any `vite build` — including Vercel
      // *preview* deployments — so that gate was always-false on Vercel
      // and made the branch unreachable for the case it was meant to fix.
      // Removed.)
      // If the env var is absent we fall through to (2) so the static
      // legacy `actionpadel-bokningssystem.vercel.app` row still resolves.
      // RLS via auth still enforces tenancy regardless of resolved org_id.
      if (hostname.endsWith(".vercel.app")) {
        const previewSlug = import.meta.env.VITE_PREVIEW_DEFAULT_ORG_SLUG as
          | string
          | undefined;
        if (previewSlug) {
          return fetchOrgBySlug(previewSlug);
        }
      }

      // (2) Exact hostname-lookup via organization_domains_public view (F-2a)
      // (Legacy Vercel-URL actionpadel-bokningssystem.vercel.app ligger
      //  också i tabellen sedan F-1 — ingen separat kod-väg behövs.)
      // Viktigt: använd viewn, inte tabellen. F-2a blockerar anon från
      // direkt tabell-access för att skydda verification_token.
      const { data: exactRow, error: exactErr } = await supabase
        .from("organization_domains_public" as any)
        .select("organization_id")
        .eq("hostname", hostname)
        .eq("pattern_type", "exact")
        .maybeSingle();

      if (exactErr) {
        console.error("[usePublicOrg] exact lookup failed:", exactErr.message);
        return null;
      }

      if (exactRow) {
        return fetchOrgById((exactRow as { organization_id: string }).organization_id);
      }

      // (3) Wildcard-fallback (F-5): hämta alla wildcard-rader och prova regex.
      // Patternformat: `*.boply.app` → matchar `<slug>.boply.app` (ett-segment-slug,
      // inte multi-segment). Matchad slug slås upp i organizations.slug.
      // Via viewn (F-2a), samma anledning som ovan.
      const { data: wildcardRows, error: wcErr } = await supabase
        .from("organization_domains_public" as any)
        .select("hostname")
        .eq("pattern_type", "wildcard");

      if (wcErr) {
        console.error("[usePublicOrg] wildcard lookup failed:", wcErr.message);
        return null;
      }

      if (wildcardRows && wildcardRows.length > 0) {
        for (const row of wildcardRows as { hostname: string }[]) {
          // F-5a: Escape ALLA regex-metacharacters (inte bara `.`) och byt sedan
          // `*` mot capture-group. Admin-kontrollerat input, men ofullständig
          // escape kan leda till oavsiktliga matchningar om hostname innehåller
          // `+`, `?`, `(`, `[` osv. Måste vara identisk med edge function
          // signup-with-customer (F-4a) för konsekvent org-resolving.
          const escaped = row.hostname
            .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
            .replace(/\*/g, "([a-z0-9-]+)");
          const regex = new RegExp(`^${escaped}$`);
          const match = hostname.match(regex);
          if (match && match[1]) {
            const slug = match[1];
            const orgBySlug = await fetchOrgBySlug(slug);
            if (orgBySlug) return orgBySlug;
          }
        }
      }

      return null;
    },
    staleTime: Infinity, // hostname ändras inte under session
    retry: 1,
  });
}
